import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _ from "lodash";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import moment from "moment";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import PromocodeApi from "Services/Promocode";

const PromocodeUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState([]);

  // Fetch Promocode Details
  const getDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    if (id) {
      PromocodeApi.getPromocodeDetails(id).then(onSuccess, onFailure);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog) {
      getDetails();
    }
  }, [openDialog, getDetails]);

  const handleUpdatePromocode = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = {
      promoCode: formValues?.promoCode?.toUpperCase() || "",
      discount: formValues?.discount,
      discountType: formValues?.discountType,
      discountUserType: formValues?.discountUserType,
      startingAt: formValues?.startingAt,
      expiresAt: formValues?.expiresAt,
      use_count: formValues?.use_count,
      description: formValues?.description,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "Promocode Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while update promocode",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    PromocodeApi.updatePromocode(id, { ...data }).then(onSuccess, onFailure);
  };

  const handlestartingdate = (newValue) => {
    if (newValue === null || newValue === "") {
      formik.setFieldValue("startingAt", newValue);
    } else {
      const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
      const parsedDate = moment(selectedDate);
      const formattedDate = parsedDate.format("MM/DD/YYYY HH:mm:ss");
      formik.setFieldValue("startingAt", formattedDate);
    }
  };

  const handleexpiredate = (newValue) => {
    if (newValue === null || newValue === "") {
      formik.setFieldValue("expiresAt", newValue);
    } else {
      const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
      const parsedDate = moment(selectedDate);
      const formattedDate = parsedDate.format("MM/DD/YYYY HH:mm:ss");
      formik.setFieldValue("expiresAt", formattedDate);
    }
  };

  const parseDate = (value) => {
    if (!value) return "";
    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.format("MM/DD/YYYY HH:mm:ss") : "";
  };

  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("promoCode", details?.promoCode || "");
      formik.setFieldValue("discount", details?.discount || "");
      formik.setFieldValue("discountType", details?.discountType || "");
      formik.setFieldValue("discountUserType", details?.discountUserType || "");
      formik.setFieldValue("startingAt", parseDate(details?.startingAt));
      formik.setFieldValue("expiresAt", parseDate(details?.expiresAt));
      formik.setFieldValue("use_count", details?.use_count || "");
      formik.setFieldValue("description", details?.description || "");
    }
  }, [details]);

  const formik = useFormik({
    initialValues: {
      promoCode: "",
      discount: "",
      discountType: "",
      discountUserType: "",
      startingAt: "",
      expiresAt: "",
      use_count: "",
      description: "",
    },
    validationSchema: yup.object({
      promoCode: yup.string().required("PromoCode is required"),
      discount: yup
        .number()
        .typeError("Discount must be a number")
        .required("Discount is required"),
      discountType: yup.string().required("Discount Type is required"),
      discountUserType: yup.string().required("Discount User Type is required"),
      startingAt: yup
        .string()
        .required("Starting time is required")
        .test(
          "is-today-or-future",
          "Starting date must be today or a future date",
          (value) => {
            if (!value) return false;
            const selectedDate = moment(value, "MM/DD/YYYY HH:mm:ss", true);
            if (!selectedDate.isValid()) return false;
            return selectedDate.isSameOrAfter(moment(), "day");
          }
        ),
      expiresAt: yup
        .string()
        .required("Expiring time is required")
        .test(
          "is-future-date",
          "Expiry date must be in the future",
          (value) => {
            if (!value) return false;
            const selectedDate = moment(value, "MM/DD/YYYY HH:mm:ss", true);
            if (!selectedDate.isValid()) return false;
            return selectedDate.isAfter(moment());
          }
        )
        .test(
          "is-after-startingAt",
          "Expiry date must be greater than Starting date",
          function (value) {
            const { startingAt } = this.parent;
            if (!value || !startingAt) return true;
            const start = moment(startingAt, "MM/DD/YYYY HH:mm:ss", true);
            const expire = moment(value, "MM/DD/YYYY HH:mm:ss", true);
            if (!start.isValid() || !expire.isValid()) return false;
            return expire.isAfter(start);
          }
        ),
      use_count: yup
        .number()
        .typeError("Usage count must be a number")
        .required("Usage count is required"),
      description: yup.string().required("Description is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdatePromocode,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Update Promo Code</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Promo Code</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Promo Code */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Promo Code</Typography>
              <TextField
                name="promoCode"
                placeholder="e.g. SAVE50"
                value={formik.values.promoCode}
                onChange={formik.handleChange}
                error={formik.touched.promoCode && Boolean(formik.errors.promoCode)}
                helperText={formik.touched.promoCode && formik.errors.promoCode}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Discount */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Discount</Typography>
              <TextField
                name="discount"
                placeholder="e.g. 20"
                value={formik.values.discount}
                onChange={formik.handleChange}
                error={formik.touched.discount && Boolean(formik.errors.discount)}
                helperText={formik.touched.discount && formik.errors.discount}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Discount Type */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Discount Type</Typography>
              <TextField
                select
                name="discountType"
                value={formik.values.discountType}
                onChange={formik.handleChange}
                error={formik.touched.discountType && Boolean(formik.errors.discountType)}
                helperText={formik.touched.discountType && formik.errors.discountType}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (val) =>
                    val ? (val === "PERCENTAGE" ? "Percentage" : "Flat") : <span style={{ color: "#94a3b8" }}>Select Discount Type</span>,
                }}
              >
                <MenuItem value="" disabled>Select Discount Type</MenuItem>
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                <MenuItem value="FLAT">Flat</MenuItem>
              </TextField>
            </Grid>

            {/* User Type */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>User Type</Typography>
              <TextField
                select
                name="discountUserType"
                value={formik.values.discountUserType}
                onChange={formik.handleChange}
                error={formik.touched.discountUserType && Boolean(formik.errors.discountUserType)}
                helperText={formik.touched.discountUserType && formik.errors.discountUserType}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (val) =>
                    val ? (val === "ALL" ? "All Users" : "New Users") : <span style={{ color: "#94a3b8" }}>Select User Type</span>,
                }}
              >
                <MenuItem value="" disabled>Select User Type</MenuItem>
                <MenuItem value="ALL">All Users</MenuItem>
                <MenuItem value="NEW">New Users</MenuItem>
              </TextField>
            </Grid>

            {/* Starting At */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Starting At</Typography>
              <Box className={classes.datePickerStyle}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formik.values.startingAt ? dayjs(formik.values.startingAt, "MM/DD/YYYY HH:mm:ss") : null}
                    onChange={(newValue) => handlestartingdate(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={formik.touched.startingAt && Boolean(formik.errors.startingAt)}
                        helperText={formik.touched.startingAt && formik.errors.startingAt}
                        size="small"
                        fullWidth
                        variant="outlined"
                        className={classes.formInput}
                      />
                    )}
                    slotProps={{
                      textField: {
                        error: formik.touched.startingAt && Boolean(formik.errors.startingAt),
                        helperText: formik.touched.startingAt && formik.errors.startingAt,
                        size: "small",
                        fullWidth: true,
                        variant: "outlined",
                        className: classes.formInput,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>

            {/* Expiring At */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Expiring At</Typography>
              <Box className={classes.datePickerStyle}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formik.values.expiresAt ? dayjs(formik.values.expiresAt, "MM/DD/YYYY HH:mm:ss") : null}
                    onChange={(newValue) => handleexpiredate(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={formik.touched.expiresAt && Boolean(formik.errors.expiresAt)}
                        helperText={formik.touched.expiresAt && formik.errors.expiresAt}
                        size="small"
                        fullWidth
                        variant="outlined"
                        className={classes.formInput}
                      />
                    )}
                    slotProps={{
                      textField: {
                        error: formik.touched.expiresAt && Boolean(formik.errors.expiresAt),
                        helperText: formik.touched.expiresAt && formik.errors.expiresAt,
                        size: "small",
                        fullWidth: true,
                        variant: "outlined",
                        className: classes.formInput,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>

            {/* Usage Count */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Usage Count</Typography>
              <TextField
                name="use_count"
                placeholder="e.g. 100"
                value={formik.values.use_count}
                onChange={formik.handleChange}
                error={formik.touched.use_count && Boolean(formik.errors.use_count)}
                helperText={formik.touched.use_count && formik.errors.use_count}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Description</Typography>
              <TextField
                name="description"
                placeholder="Enter promo description"
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
                className={classes.formInput}
              />
            </Grid>
          </Grid>

          <Box className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              onClick={() => {
                formik.resetForm();
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
            >
              <EditOutlined style={{ fontSize: "18px" }} />
              Save Changes
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PromocodeUpdate;
